import React, { useEffect, useMemo } from 'react'
import useClientCards from '@/src/hooks/data/useClientCards'
// import GameCard from '../../GameCard/GameCard';
import { GameCard } from '@themed-components';
import styles from './A23ClientCards.module.css';
import { 
  usePlayerSeat as useGamePlayerSeat,
  useGameDefinition,
  useShowAllCards,
  useGameEnded,
  useIsTourneyGame,
  useIsUserWinner,
  useShownCards,
  useGameId
} from '@/src/hooks/data/useGameTable';
import { doesExist } from '../../../../Common/utils/GameUtils';
import { CLIENT_CARDS, L_CARD, M_CARD, TEXAS_HOLDEM } from '@/Poker/data/Constants';
import { IoEye, IoEyeOff } from "react-icons/io5";
import { setShownCards, toggleShowAllCards } from '@/Poker/redux/slices/gameTableSlice';
import useGameSocket from '@/src/hooks/utils/useGameSocket';
import { useDispatch } from 'react-redux';
import useTableSettings from '@/src/hooks/data/useTableSettings';
import { AnimatePresence, LayoutGroup, motion } from 'motion/react';
import { FLUSH, FOUR_OF_A_KIND, FULL_HOUSE, HIGH_CARD, ONE_PAIR, ROYAL_FLUSH, STRAIGHT, STRAIGHT_FLUSH, THREE_OF_A_KIND, TWO_PAIRS } from '@/Poker/data/Constants';
import useApplyTableSettings from '@/src/hooks/data/useApplyTableSettings';

export default function A23ClientCards({ position, tempTableId, possibleWinningHandType }) {
    const dispatch = useDispatch();
    const clientCards = useClientCards(tempTableId);
    const playerSeat = useGamePlayerSeat(tempTableId);
    const gameDefinition = useGameDefinition(tempTableId);
    const gameType = gameDefinition?.gameType;
    const showAllCards = useShowAllCards(tempTableId);
    const gameEnded = useGameEnded(tempTableId);
    const isTourneyGame = useIsTourneyGame(tempTableId);
    const isUserWinner = useIsUserWinner(tempTableId);
    const shownCards = useShownCards(tempTableId);
    const gameId = useGameId(tempTableId);
    const { sendShowCardsProtocol } = useGameSocket(tempTableId);
    const { tableSettings: { autoMuck, showHandStrength, sortCardsByRank } } = useTableSettings();
    const { updateCardSortingSetting } = useApplyTableSettings();

    const handleLongPress = () => {
        // updateCardSortingSetting();
    };

    // Sort cards by rank if enabled
    const sortedClientCards = useMemo(() => {
        if (!clientCards || clientCards.length === 0) return [];
        
        if (sortCardsByRank) {
            return [...clientCards].sort((a, b) => {
                const valueA = parseInt(a.slice(1)) || 0;
                const valueB = parseInt(b.slice(1)) || 0;
                
                // Handle Ace (1) as highest value for sorting
                const rankA = valueA === 1 ? 14 : valueA;
                const rankB = valueB === 1 ? 14 : valueB;
                
                return rankA - rankB;
            });
        }
        
        return clientCards;
    }, [clientCards, sortCardsByRank]);

    const returnHandStrengthPercentage = () => {
        switch (possibleWinningHandType) {
            case HIGH_CARD:
                return 10;
            case ONE_PAIR:
                return 20
            case TWO_PAIRS:
                return 30
            case THREE_OF_A_KIND:
                return 40
            case STRAIGHT:
                return 50
            case FLUSH:
                return 60
            case FULL_HOUSE:
                return 70
            case FOUR_OF_A_KIND:
                return 80
            case STRAIGHT_FLUSH:
                return 90
            case ROYAL_FLUSH:
                return 100
        }
    };

    const returnCardSize = () => {
        switch (gameType) {
            case TEXAS_HOLDEM:
                return M_CARD;
            default:
                return M_CARD;
        }
    }

    const returnCardLeftPosition = (index) => {
        switch (gameType) {
            case TEXAS_HOLDEM:
                return { marginLeft: `${index * -25}%` };
            default:
                return { marginLeft: index === 0 ? 0 : -22 };
        }
    }

    if (!doesExist(playerSeat) || clientCards.length === 0 || position !== playerSeat) {
        return null
    }

    const handleToggleShowAllCards = () => {
        // if (!autoMuck && !isFolded) return;
        if ((showAllCards && gameEnded) || isTourneyGame || isUserWinner) {
            return;
        }
        dispatch(
            toggleShowAllCards({ tempTableId: tempTableId, playerCards: clientCards })
        );
        if (gameEnded) {
            sendShowCardsProtocol(clientCards);
        }
    };

    const handleCardShow = (card) => {
        // if (!autoMuck && !isFolded) return;
        if (
            (showAllCards && gameEnded) ||
            (shownCards.includes(card) && gameEnded) ||
            isTourneyGame ||
            isUserWinner
        ) {
            return;
        }
        dispatch(
            setShownCards({
                tempTableId: tempTableId,
                card: card,
                playerCards: clientCards,
            })
        );
        if (gameEnded) {
            sendShowCardsProtocol([...shownCards, card]);
        }
    };

    return (
        <div className={styles.clientCardsContainer}>
            <LayoutGroup>
                <motion.div
                    layout={true}
                    className={styles.clientCardsWrapper}
                    onPan={handleLongPress}
                >
                    <LayoutGroup>
                        {sortedClientCards.map((card, index) => (
                            <GameCard
                                layout={true}
                                showAllCards={showAllCards}
                                shownCard={shownCards.includes(card)}
                                tempTableId={tempTableId}
                                style={returnCardLeftPosition(index)}
                                size={returnCardSize()}
                                type={CLIENT_CARDS}
                                gameType={gameType}
                                index={index}
                                onClick={() => handleCardShow(card)}
                                value={card}
                                key={card + gameId} />
                        ))}
                    </LayoutGroup>
                </motion.div>
                <AnimatePresence
                    className={styles.handStrengthContainer}>
                    {
                        showHandStrength && possibleWinningHandType && !gameEnded && (
                            <motion.div
                                initial={{
                                    scale: 0,
                                }}
                                animate={{
                                    scale: 1,
                                }}
                                layout={true}
                                className={styles.handStrengthText}>
                                {possibleWinningHandType}
                            </motion.div>
                        )
                    }
                    {
                        showHandStrength && possibleWinningHandType && !gameEnded && (
                            <motion.div
                                layout={true}
                                className={styles.handStrengthProgressBar}>
                                {
                                    possibleWinningHandType && !gameEnded ?
                                        <motion.div
                                            initial={{
                                                width: '0%',
                                            }}
                                            animate={{
                                                width: `${100 - returnHandStrengthPercentage()}%`,
                                            }}
                                            className={styles.handStrengthProgressBarFill}></motion.div>
                                        :
                                        null
                                }
                            </motion.div>
                        )
                    }
                </AnimatePresence>
                {
                    clientCards &&
                        clientCards.length > 0 &&
                        !isTourneyGame &&
                        !isUserWinner
                        ?
                        <div className={styles.showCardsButtonContainer}>
                            <div onClick={handleToggleShowAllCards} className={styles.showCardsButton}>
                                {showAllCards ? <IoEye color='var(--color-focus)' /> : <IoEyeOff color='black' />}
                            </div>
                        </div>
                        : null
                }
            </LayoutGroup>
        </div>
    )
}
